// The currency a reader's amounts are in, as it appears INSIDE UI sentences (owner Q18, 2026-10-03):
// the staff app follows the active company's currency, the client portal the merchant's own.
// No string names a currency. A value prints
//   {{cur}}          the ISO code — "Unit price (THB)"
//   {{curSym}}       the symbol glued to an amount — "฿0" (the code when the region has no symbol)
//   $t(cur_word)     the word in the UI language — "baht" / "泰铢" / "บาท"
//   $t(cur_adj)      the attributive word — "a baht total", "a Singapore dollar total"
//   $t(cur_unit)     a unit label the same in every language — THB: "บาท", any other: the code
// The two variables live in i18next's interpolation.defaultVariables (like {{brand}}, utils/brand.ts).
// The three words are looked up BY {{cur}} in the table of src/i18n/mt/curwords.ts (cw_ / cwa_ / cwu_
// + code), so a word can never name another currency than the code beside it. A code not listed there
// prints itself; no currency at all prints '¤'.
// A label whose amounts are in ONE known currency that is not the reader's (the firm's bills in the
// portal, a document of another company, the currency an FX rate converts into) passes
// currencyArgs(code, symbol) to its own t() call: {{cur}}, {{curSym}} and the three words then all
// name that currency, and no other call site moves.
// ⛔ Thai statutory text (ภ.ง.ด. / ภ.พ. form units, Thai-law thresholds, Revenue Code notes) keeps
// naming the baht and never uses these.
// Imports nothing but i18next, so src/i18n/index.ts can call it without an import cycle.
import i18next from 'i18next';

type CurrencyRegion = { currency?: string | null; currencySymbol?: string | null; country?: string | null };

const LANGS = ['en', 'zh', 'th'];
const NONE = '¤';   // no currency known yet (before /api/me, or a workspace with no region_currency)
let code = '';
let symbol = '';
let applied: string | null = null;   // null until the first call (made by src/i18n/index.ts at init)

// The three words, resolved on every t() call from the {{cur}} of THAT call (its own, else the
// reader's default): the UI language's word, else the English one (fallbackLng), else the code itself.
const WORDS: Record<string, string> = {
  cur_word: '$t(cw_{{cur}}, {"defaultValue": "{{cur}}"})',
  cur_adj: '$t(cwa_{{cur}}, {"defaultValue": "{{cur}}"})',
  // the unit label is one string for every language: never another language's word
  cur_unit: '$t(cwu_{{cur}}, {"lng": "en", "defaultValue": "{{cur}}"})',
};

/** The interpolation values that name ONE given currency: spread into a single t() call whose
 *  amounts are in that currency rather than the reader's — t('p_bills_th_total', currencyArgs(currencyCode())).
 *  The symbol falls back to the code, and no code at all to '¤', exactly as the reader's own do. */
export function currencyArgs(c?: string | null, s?: string | null): { cur: string; curSym: string } {
  const k = String(c || '').trim().toUpperCase();
  return { cur: k || NONE, curSym: String(s || '').trim() || k || NONE };
}
/** The two interpolation variables for the current currency — src/i18n/index.ts starts i18next with them. */
export const currencyVars = (): { cur: string; curSym: string } => currencyArgs(code, symbol);
/** The ISO code the UI strings print ('' when none is known). */
export const currencyWordCode = (): string => code;

/** Point every UI string at this region's currency (its `currency` + `currencySymbol`) and make mounted
 *  screens translate again. Called with the /api/me region (store/index.ts) and, in the client portal,
 *  with the merchant's own region (profile.docRegion). */
export function setCurrencyWords(r?: CurrencyRegion | null): void {
  code = String(r?.currency || '').trim().toUpperCase();
  symbol = String(r?.currencySymbol || '').trim();
  const sig = `${code}|${symbol}`;
  if (sig === applied || !i18next.isInitialized) return;
  const dv = (i18next.options?.interpolation as any)?.defaultVariables;
  if (dv && typeof dv === 'object') Object.assign(dv, currencyVars());   // in place: i18next reads this very object
  for (const l of LANGS) for (const [k, v] of Object.entries(WORDS)) i18next.addResource(l, 'translation', k, v);
  const first = applied === null;
  applied = sig;
  if (first) return;   // the initial call happens before anything renders
  Promise.resolve().then(() => { try { i18next.emit('languageChanged', i18next.language); } catch { /* nothing mounted */ } });
}

/** The portal's Thai-baht explanations (Revenue Code s.86/4) are for Thai merchants only (owner Q18). */
export const isThaiCountry = (r?: CurrencyRegion | null): boolean => String(r?.country || '').trim().toUpperCase() === 'TH';
